import { useState, useMemo } from 'react'
import { Search, Calendar, User, CheckCircle2 } from 'lucide-react'
import { clsx } from 'clsx'
import { Dialog, DialogHeader } from '@/components/ui/Dialog'
import type { SharedTask } from '@/lib/sharedTypes'

interface TaskPickerForChatProps {
  isOpen: boolean
  onClose: () => void
  tasks: SharedTask[]
  onSelect: (task: SharedTask) => void
}

const priorityColors: Record<string, string> = {
  high: 'border-l-red-500',
  medium: 'border-l-amber-500',
  low: 'border-l-blue-400',
  none: 'border-l-gray-300',
}

export function TaskPickerForChat({
  isOpen,
  onClose,
  tasks,
  onSelect,
}: TaskPickerForChatProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)

  const filteredTasks = useMemo(() => {
    if (!searchQuery.trim()) return tasks
    const q = searchQuery.toLowerCase()
    return tasks.filter((t) => t.title.toLowerCase().includes(q))
  }, [tasks, searchQuery])

  const handleConfirm = () => {
    const task = tasks.find((t) => t.id === selectedTaskId)
    if (task) {
      onSelect(task)
      setSelectedTaskId(null)
      setSearchQuery('')
      onClose()
    }
  }

  const handleClose = () => {
    setSelectedTaskId(null)
    setSearchQuery('')
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} size="md">
      <DialogHeader title="할일 공유" onClose={handleClose} />

      {/* Search */}
      <div className="mt-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-caption" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="할일 검색..."
          className="w-full pl-9 pr-3 py-2 text-sm bg-surface-secondary border border-base rounded-lg outline-none text-heading placeholder:text-disabled focus:border-primary-400"
        />
      </div>

      {/* Task list */}
      <div className="mt-3 max-h-72 overflow-y-auto space-y-1.5">
        {filteredTasks.length === 0 && (
          <p className="text-sm text-caption text-center py-8">
            {searchQuery ? '검색 결과가 없습니다' : '할일이 없습니다'}
          </p>
        )}

        {filteredTasks.map((task) => {
          const isSelected = selectedTaskId === task.id
          const isCompleted = task.status === 'completed'

          return (
            <button
              key={task.id}
              onClick={() => setSelectedTaskId(isSelected ? null : task.id)}
              className={clsx(
                'w-full text-left p-3 rounded-lg border border-l-4 transition-colors',
                priorityColors[task.priority],
                isSelected
                  ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-base hover:bg-surface-secondary'
              )}
            >
              <div className="flex items-start gap-2">
                <div
                  className={clsx(
                    'mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                    isSelected
                      ? 'border-primary-500 bg-primary-500'
                      : 'border-base'
                  )}
                >
                  {isSelected && (
                    <CheckCircle2 className="w-3 h-3 text-white" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p
                    className={clsx(
                      'text-sm font-medium truncate',
                      isCompleted ? 'text-caption line-through' : 'text-heading'
                    )}
                  >
                    {task.title}
                  </p>

                  <div className="flex items-center gap-2 mt-0.5 text-meta text-caption">
                    <span
                      className={clsx(
                        'px-1.5 py-0.5 rounded font-medium',
                        isCompleted
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-surface-tertiary'
                      )}
                    >
                      {isCompleted ? '완료' : '진행 중'}
                    </span>

                    {(() => {
                      const names = task.assigneeNames?.length ? task.assigneeNames : task.assigneeName ? [task.assigneeName] : []
                      return names.length > 0 && (
                        <span className="flex items-center gap-0.5">
                          <User className="w-3 h-3" />
                          {names.length > 2 ? `${names[0]} 외 ${names.length - 1}명` : names.join(', ')}
                        </span>
                      )
                    })()}

                    {task.dueDate && (
                      <span className="flex items-center gap-0.5">
                        <Calendar className="w-3 h-3" />
                        {task.dueDate}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Footer */}
      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={handleClose}
          className="px-4 py-2 text-sm text-caption hover:text-heading transition-colors rounded-lg"
        >
          취소
        </button>
        <button
          onClick={handleConfirm}
          disabled={!selectedTaskId}
          className={clsx(
            'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
            selectedTaskId
              ? 'bg-primary-500 text-white hover:bg-primary-600'
              : 'bg-surface-tertiary text-disabled cursor-not-allowed'
          )}
        >
          공유
        </button>
      </div>
    </Dialog>
  )
}
